import { useCallback, useId, useRef, useState } from "react"
import { CircleNotch } from "@phosphor-icons/react"
import { cva } from "class-variance-authority"
import { t } from "i18next"
import { FormError } from "@/components/ui/form"
import { cn } from "@/lib/utils"

export const inputVariant = cva(
  "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-subdued-hovered focus:outline-primary z-1 w-full rounded-[8px] border px-3.75 pt-4.75 text-sm outline-offset-0 transition-all focus:outline-2",
  {
    variants: {
      type: {
        input: "h-12 truncate",
        textarea:
          "resize-vertical scrollbar scrollbar-thumb-rounded-full scrollbar-w-1.5 group-hover/scrollbar:scrollbar-thumb-surface-hovered hover:scrollbar-thumb-surface-pressed min-h-17 px-3.75 pt-5.25 pb-1 leading-5",
        custom: "resize-vertical min-h-17 px-3.75 pt-5.25 pb-1 leading-5",
      },
    },
    defaultVariants: {
      type: "input",
    },
  },
)

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  assistiveText?: string | React.ReactNode
  error?: string
  translateError?: boolean
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>
  testId?: string
  errorClasses?: React.HTMLAttributes<HTMLParagraphElement>["className"]
  isLoading?: boolean
  ref?: React.Ref<HTMLInputElement>
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  assistiveText?: string | React.ReactNode
  error?: string
  translateError?: boolean
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>
  errorClasses?: React.HTMLAttributes<HTMLParagraphElement>["className"]
  "data-testid"?: string
  textLimit?: number
  ref?: React.Ref<HTMLTextAreaElement>
}

export const Input = ({
  className,
  label,
  assistiveText,
  error,
  translateError,
  required,
  wrapperProps,
  testId = "field",
  errorClasses,
  children,
  isLoading = false,
  ...props
}: InputProps) => {
  const id = useId()

  return (
    <div className={cn(wrapperProps?.className, "relative w-full")} data-testid={testId}>
      {isLoading && (
        <label htmlFor={id} className="absolute top-1 right-1 p-2">
          <CircleNotch className="text-on-surface-subdued animate-spin" />
        </label>
      )}
      <input
        id={id}
        data-testid={`${testId}-input`}
        autoComplete="off"
        aria-autocomplete="none"
        placeholder=" "
        className={cn(inputVariant(), className, error && "outline-critical! outline-1")}
        {...props}
      />
      <label
        data-testid={`${testId}-label`}
        htmlFor={id}
        className={cn(
          "text-on-surface-subdued peer-focus:text-primary absolute top-1 left-4 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
          error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
        )}>
        {label}
        {required && "*"}
      </label>
      {assistiveText && !error && (
        <p className="text-on-surface-subdued mt-0.75 ml-4 text-xs tracking-normal">{assistiveText}</p>
      )}
      {error && (
        <FormError data-testid={`${testId}-error-message`} className={errorClasses}>
          {translateError ? t(error) : error}
        </FormError>
      )}
      {children}
    </div>
  )
}

export const TextArea = ({
  className,
  label,
  assistiveText,
  error,
  required,
  wrapperProps,
  translateError,
  errorClasses,
  "data-testid": testId,
  onChange,
  textLimit,
  ref: forwardedRef,
  ...props
}: TextareaProps) => {
  const id = useId()
  const internalRef = useRef<HTMLTextAreaElement | null>(null)
  const [value, setValue] = useState("")

  const resizeHeight = useCallback(() => {
    const current = internalRef?.current
    if (current) {
      current.style.height = "auto"
      const { scrollHeight, offsetHeight, clientHeight } = current
      const yBorderWidth = offsetHeight - clientHeight
      current.style.height = `${scrollHeight + yBorderWidth}px`
    }
  }, [internalRef])

  return (
    <div className={cn(wrapperProps?.className, "relative w-full")}>
      <textarea
        data-testid={`${testId}-input`}
        id={id}
        ref={(e) => {
          if (forwardedRef) {
            if (typeof forwardedRef === "function") {
              forwardedRef(e)
            } else if (forwardedRef.current) {
              forwardedRef.current = e
            }
          }
          internalRef.current = e
          setValue(internalRef.current?.value ?? "")
          resizeHeight()
        }}
        autoComplete="off"
        aria-autocomplete="none"
        placeholder=" "
        className={cn(inputVariant({ type: "textarea" }), className, error && "outline-critical! outline")}
        {...props}
        onChange={(e) => {
          onChange?.(e)
          resizeHeight()
          setValue(e.target.value)
        }}
      />
      {label && (
        <label
          data-testid={`${testId}-label`}
          htmlFor={id}
          onClick={() => internalRef.current?.focus()}
          className={cn(
            "bg-surface-subdued text-on-surface-subdued absolute left-4 z-2 w-[calc(100%-30px)] border-t pt-1 text-start text-xs leading-4 tracking-[0.25px] transition-all",
            "peer-hover:bg-surface-subdued-hovered peer-focus:text-primary peer-placeholder-shown:pt-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:leading-5 peer-placeholder-shown:tracking-normal peer-focus:pt-1 peer-focus:text-xs peer-focus:leading-4 peer-focus:tracking-[0.25px]",
            error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
          )}>
          {label}
          {required && "*"}
        </label>
      )}
      <div className="flex w-full justify-between">
        {assistiveText && !error && (
          <p className="text-on-surface-subdued mt-1 ml-4 items-center text-xs tracking-normal">{assistiveText}</p>
        )}
        {error && <FormError className={errorClasses}>{translateError ? t(error) : error}</FormError>}
        {textLimit && (
          <p
            className={cn(
              "text-on-surface-subdued mt-1 ml-auto items-center text-xs tracking-normal",
              error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
            )}>
            {`${value.length}/${textLimit}`}
          </p>
        )}
      </div>
    </div>
  )
}
